import { Badge } from '@design-system';
import { X } from 'lucide-react';

import {
  FEATURE_BENEFITS_LABELS,
  formatBenefitDurationLabel,
  type CombinedFilterType,
  type FilterName,
  type InflationTypeFilterValue,
} from '@/features/shop-insurance';

import { formatCurrency } from '@client/lib';
import { UNDERWRITING_STATUS_CONFIG } from '@shared';

interface AdjustedQuotesBannerProps {
  premiumValue: number;
  appliedFilters: FilterName[];
  benefitDurationValue: CombinedFilterType;
  inflationProtectionValue: number;
  /** Legacy AOB/COB range; labelled instead of the minimum when the flag is off. */
  inflationProtectionRangeValue: CombinedFilterType;
  useMinimumInflation: boolean;
  inflationTypeFilterValue?: InflationTypeFilterValue;
  onRemoveFilter?: (filter: FilterName) => void;
  selectedEligibilityFilters?: string[];
  onRemoveEligibilityFilter?: (filter: string) => void;
  selectedFeatureBenefitsFilters?: string[];
  onRemoveFeatureBenefitsFilter?: (filter: string) => void;
  hiddenCarriers?: string[];
  onRemoveHiddenCarrier?: (carrier: string) => void;
}

/**
 * The premium chip reports that quotes were repriced to the selected premium rather than a
 * filter the advisor set, so there is nothing for an X to clear.
 */
const NON_REMOVABLE_FILTERS: FilterName[] = ['premium'];

function RemoveFilterButton({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <button
      onClick={onRemove}
      className="inline-flex shrink-0"
      aria-label={`Remove ${label} filter`}
      type="button"
    >
      <X className="size-3" />
    </button>
  );
}

export function AdjustedQuotesBanner({
  premiumValue,
  appliedFilters,
  benefitDurationValue,
  inflationProtectionValue,
  inflationProtectionRangeValue,
  useMinimumInflation,
  inflationTypeFilterValue,
  onRemoveFilter,
  selectedEligibilityFilters = [],
  onRemoveEligibilityFilter,
  selectedFeatureBenefitsFilters = [],
  onRemoveFeatureBenefitsFilter,
  hiddenCarriers = [],
  onRemoveHiddenCarrier,
}: AdjustedQuotesBannerProps) {
  function getFilterLabel(filter: FilterName) {
    switch (filter) {
      case 'premium':
        return `${formatCurrency(premiumValue)} Premium`;
      case 'inflationProtection':
        if (!useMinimumInflation) {
          return inflationProtectionRangeValue.type === 'combined'
            ? `${inflationProtectionRangeValue.combined[0]}%-${inflationProtectionRangeValue.combined[1]}% Inflation`
            : `AOB: ${inflationProtectionRangeValue.aob[0]}%-${inflationProtectionRangeValue.aob[1]}%, COB: ${inflationProtectionRangeValue.cob[0]}%-${inflationProtectionRangeValue.cob[1]}% Inflation`;
        }
        return `${inflationProtectionValue}%+ Inflation`;
      case 'inflationType':
        return `${inflationTypeFilterValue === 'compound' ? 'Compound' : 'Simple'} Inflation`;
      case 'benefitDuration':
        if (benefitDurationValue.type === 'combined') {
          return `${formatBenefitDurationLabel(
            benefitDurationValue.combined[0],
            benefitDurationValue.combined[1],
          )} Duration`;
        }
        return `AOB ${formatBenefitDurationLabel(
          benefitDurationValue.aob[0],
          benefitDurationValue.aob[1],
        )} / COB ${formatBenefitDurationLabel(
          benefitDurationValue.cob[0],
          benefitDurationValue.cob[1],
        )}`;
      default:
        return '';
    }
  }

  function getFeatureBenefitsLabel(filterKey: string) {
    return FEATURE_BENEFITS_LABELS[filterKey] || filterKey;
  }

  const filterOrder: FilterName[] = [
    'premium',
    'inflationProtection',
    'inflationType',
    'benefitDuration',
  ];

  const sortedFilters = [...appliedFilters].sort((filterA, filterB) => {
    return filterOrder.indexOf(filterA) - filterOrder.indexOf(filterB);
  });

  return (
    <div className="flex items-start gap-2 rounded-md border p-2.5">
      <p className="text-foreground ml-0.5 shrink-0 py-0.5 text-xs font-semibold uppercase tracking-wide">
        Active Filters:
      </p>
      <div className="flex flex-wrap gap-2">
        {/* Regular filters */}
        {sortedFilters.map(filter => {
          const filterLabel = getFilterLabel(filter);
          const isRemovable = onRemoveFilter && !NON_REMOVABLE_FILTERS.includes(filter);

          return (
            <Badge key={filter}>
              {filterLabel}
              {isRemovable && (
                <RemoveFilterButton
                  label={filterLabel}
                  onRemove={() => onRemoveFilter(filter)}
                />
              )}
            </Badge>
          );
        })}
        {/* Eligibility filters */}
        {selectedEligibilityFilters.map(filter => {
          const config =
            UNDERWRITING_STATUS_CONFIG[filter as keyof typeof UNDERWRITING_STATUS_CONFIG];
          const filterLabel = config?.label || filter;

          return (
            <Badge key={filter}>
              {filterLabel}
              {onRemoveEligibilityFilter && (
                <RemoveFilterButton
                  label={filterLabel}
                  onRemove={() => onRemoveEligibilityFilter(filter)}
                />
              )}
            </Badge>
          );
        })}
        {/* Feature Benefits filters */}
        {selectedFeatureBenefitsFilters.map(filter => {
          const filterLabel = getFeatureBenefitsLabel(filter);
          return (
            <Badge key={filter}>
              {filterLabel}
              {onRemoveFeatureBenefitsFilter && (
                <RemoveFilterButton
                  label={filterLabel}
                  onRemove={() => onRemoveFeatureBenefitsFilter(filter)}
                />
              )}
            </Badge>
          );
        })}
        {/* Hidden carriers */}
        {hiddenCarriers.map(carrier => (
          <Badge key={carrier}>
            {`${carrier} hidden`}
            {onRemoveHiddenCarrier && (
              <button
                onClick={() => onRemoveHiddenCarrier(carrier)}
                className="inline-flex shrink-0"
                aria-label={`Unhide ${carrier}`}
                type="button"
              >
                <X className="size-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
    </div>
  );
}
